import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
  TextField,
  Button,
  styled,
  alpha,
  CircularProgress,
  Select,
  MenuItem,
  Autocomplete,
  Chip
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getLendersList } from '../../API/API';

const TableWrapper = styled(Box)(({ theme }) => ({
  '& .MuiTableContainer-root': {
    borderRadius: '16px',
    border: '1px solid rgba(229, 232, 235, 0.7)',
    boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.04)',
    overflow: 'auto'
  }
}));

const TableHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2.5, 3),
  backgroundColor: theme.palette.background.paper,
  borderBottom: '1px solid rgba(229, 232, 235, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(2),
  flexWrap: 'wrap'
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  '&.MuiTableCell-head': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    borderBottom: '2px solid rgba(229, 232, 235, 0.7)'
  },
  '&.MuiTableCell-body': {
    color: theme.palette.text.primary,
    fontSize: '0.85rem',
    letterSpacing: '0.01em',
    borderBottom: '1px solid rgba(229, 232, 235, 0.5)'
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: '16px',
  border: '1px dashed rgba(0, 0, 0, 0.12)'
}));

const LenderSelectionTable = ({ selectedLenders, onSelectionChange }) => {
  const [lenders, setLenders] = useState([]);
  const [filteredLenders, setFilteredLenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [activeFilters, setActiveFilters] = useState([]);

  const filterFields = [
    { label: 'Name', value: 'name' },
    { label: 'Institution Type', value: 'institutionType' },
    { label: 'Location', value: 'location' },
    { label: 'Type of Lending', value: 'lendingType' },
    { label: 'Retail Loans', value: 'retailLoans' }
  ];

  useEffect(() => {
    const fetchLenders = async () => {
      try {
        setLoading(true);
        const response = await getLendersList();
        const lendersList = response?.result?.lenders || response?.lenders || [];
        const transformed = lendersList.map(lender => ({
          id: lender.id,
          lenderId: lender.lender_lenderId,
          name: lender.name,
          mobileNumber: lender.mobileNumber,
          email: lender.email,
          institutionType: lender.lender_financialInstitutionType,
          location: lender.lender_location || lender.address,
          lendingType: lender.lender_typeOfLending,
          retailLoans: lender.lender_typeOfRetailLoans,
          designation: lender.lender_designation,
          verified: lender.lender_verified
        }));
        setLenders(transformed);
        setFilteredLenders(transformed);
      } finally {
        setLoading(false);
      }
    };
    fetchLenders();
  }, []);

  useEffect(() => {
    let filtered = lenders;
    if (activeFilters.length > 0) {
      filtered = filtered.filter(lender =>
        activeFilters.every(f => {
          const val = (lender[f.field] || '').toString().toLowerCase();
          if (Array.isArray(f.value)) {
            if (f.value.length === 0) return true;
            return f.value.some(v => val.includes((v || '').toString().toLowerCase()));
          }
          const single = (f.value || '').toString().toLowerCase();
          return single ? val.includes(single) : true;
        })
      );
    }
    setFilteredLenders(filtered);
    setPage(0);
  }, [lenders, activeFilters]);


  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      onSelectionChange(filteredLenders.map(lender => lender.lenderId));
    } else {
      onSelectionChange([]);
    }
  };

  const handleClick = (lenderId) => {
    const newSelected = selectedLenders.includes(lenderId)
      ? selectedLenders.filter(selectedId => selectedId !== lenderId)
      : [...selectedLenders, lenderId];
    onSelectionChange(newSelected);
  };

  useEffect(() => {
    if (activeFilters.length > 0 && filteredLenders.length >= 0 && onSelectionChange) {
      const ids = filteredLenders.map(l => l.lenderId);
      const differs = !selectedLenders || selectedLenders.length !== ids.length || ids.some(id => !selectedLenders.includes(id));
      if (differs) {
        onSelectionChange(ids);
      }
    }
  }, [activeFilters, filteredLenders]);

  return (
    <TableWrapper>
      <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
        <div style={{ minWidth: 1100 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', px: 3, pt: 2.5, pb: 2 }}>
            {filterFields.map(field => {
              const options = Array.from(new Set(lenders.map(l => l[field.value]).filter(Boolean)));
              const selectedValues = activeFilters.find(f => f.field === field.value)?.value || [];

              const filterOptions = (opts, state) => {
                const query = (state.inputValue || '').toLowerCase();
                if (!query) return opts;
                const starts = [];
                const contains = [];
                opts.forEach(opt => {
                  const val = (opt || '').toString().toLowerCase();
                  if (val.startsWith(query)) starts.push(opt); else if (val.includes(query)) contains.push(opt);
                });
                return [...starts, ...contains];
              };

              return (
                <Autocomplete
                  key={field.value}
                  multiple
                  options={options}
                  value={selectedValues}
                  filterOptions={filterOptions}
                  onChange={(event, values) => {
                    setActiveFilters(prev => {
                      const others = prev.filter(f => f.field !== field.value);
                      const selected = Array.isArray(values) ? values : (values ? [values] : []);
                      return selected.length ? [...others, { field: field.value, value: selected }] : others;
                    });
                  }}
                  size="small"
                  disableCloseOnSelect
                  filterSelectedOptions
                  autoHighlight
                  openOnFocus
                  getOptionLabel={(option) => (option ?? '').toString()}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField {...params} label={field.label} placeholder={field.label} />
                  )}
                  sx={{ minWidth: 200, flex: '0 1 240px' }}
                />
              );
            })}
            {activeFilters.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setActiveFilters([])}
                sx={{ height: 40 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </div>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : filteredLenders.length === 0 ? (
          <EmptyState>
            <Chip label="No lenders available" />
          </EmptyState>
        ) : (
          <>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedLenders.length > 0 && selectedLenders.length < filteredLenders.length}
                      checked={filteredLenders.length > 0 && selectedLenders.length === filteredLenders.length && filteredLenders.every(l => selectedLenders.includes(l.lenderId))}
                      onChange={handleSelectAllClick}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                    />
                  </StyledTableCell>
                  <StyledTableCell>User ID</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Mobile Number</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Institution Type</StyledTableCell>
                  <StyledTableCell>Location</StyledTableCell>
                  <StyledTableCell>Type of Lending</StyledTableCell>
                  <StyledTableCell>Retail Loans</StyledTableCell>
                  <StyledTableCell>Designation</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLenders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((lender) => {
                    const isSelected = selectedLenders.includes(lender.lenderId);
                    return (
                      <TableRow
                        hover
                        onClick={() => handleClick(lender.lenderId)}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={-1}
                        key={lender.lenderId}
                        selected={isSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <StyledTableCell padding="checkbox">
                          <Checkbox checked={isSelected} icon={<CheckBoxOutlineBlankIcon />} checkedIcon={<CheckBoxIcon />} />
                        </StyledTableCell>
                        <StyledTableCell>{lender.id}</StyledTableCell>
                        <StyledTableCell>{lender.name}</StyledTableCell>
                        <StyledTableCell>{lender.mobileNumber}</StyledTableCell>
                        <StyledTableCell>
                          <a href={`mailto:${lender.email}`} className="email">{lender.email}</a>
                        </StyledTableCell>
                        <StyledTableCell>{lender.institutionType}</StyledTableCell>
                        <StyledTableCell>{lender.location}</StyledTableCell>
                        <StyledTableCell>{lender.lendingType}</StyledTableCell>
                        <StyledTableCell>{lender.retailLoans}</StyledTableCell>
                        <StyledTableCell>{lender.designation}</StyledTableCell>
                        <StyledTableCell>
                          <Chip label={lender.verified ? 'Verified' : 'Unverified'} color={lender.verified ? 'success' : 'warning'} size="small" variant="outlined" />
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredLenders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </TableContainer>
    </TableWrapper>
  );
};

export default LenderSelectionTable;


